import { useMsal } from "@azure/msal-react";
import { Formik } from "formik";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useResendEmailOtpMutation,
  useVerifyEmailOtpMutation,
} from "../../../api/network/authApiService";
import {
  serializeErrorKeyValues,
  serverErrorMessage,
} from "../../../api/network/errorCodes";
import { TPreLoginUserData } from "../../../api/store/commonSlice";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../../api/types/Onboarding";
import svgIcons from "../../../assets/svg/svgIcons";
import {
  LandingFormFieldInput,
  LandingFormFieldSubmit,
} from "../../../components/landing/formFields";
import LandingWrapper from "../../../components/landing/landing";
import { APP_CONFIG } from "../../../constants/constants";
import { useLoginHandler } from "../../../hooks/loginHandler";
import { routeUrls } from "../../../navigation/routeUrls";
import { ResendCode } from "./resend-code";
import {
  formInitialValues,
  TFormFieldNames,
  YupValidationSchema,
} from "./validation";

const ScreenVerify2FA = () => {
  const { verifyMethod } = useParams<{ verifyMethod: "email" | "mobile" }>();
  const { t } = useTranslation("translation", {
    keyPrefix: verifyMethod === "mobile" ? "verify2FAMobile" : "verify2FAEmail",
  });
  const navigate = useNavigate();

  const { accounts: msalAccounts, inProgress: msalInProgress } = useMsal();
  const { onUserTokenRetrievedSetupSessionNRedirect } = useLoginHandler({
    screen: "verify2FA",
  });

  // focus field on load
  const fieldRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    fieldRef.current?.focus();
  }, []);

  const preLoginUserData: TPreLoginUserData = useSelector(
    (state: any) => state.commonReducer.preLoginUserData
  );
  useEffect(() => {
    if (verifyMethod === "email" && !preLoginUserData?.email) {
      toast.error(t("toast.no_email"));
      navigate(routeUrls.loginPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [resendEmailOtpApiTrigger, { isLoading: isLoadingResendEmailOtp }] =
    useResendEmailOtpMutation();
  const [verifyEmailOtpApiTrigger, { isLoading: isLoadingVerifyEmailOtp }] =
    useVerifyEmailOtpMutation();

  // The reason the last resend was refused (e.g. no sign-in pending), shown by resendErrorCallback.
  const resendErrorMessage = useRef<string | undefined>();

  const handleResendCode = (successCallback: any, errorCallback: any) => {
    if (APP_CONFIG.TOASTS.INFO) toast.info(t("toast.resending_code"));
    resendEmailOtpApiTrigger({
      email: preLoginUserData?.email || "",
      ...(verifyMethod === "mobile" && { phone: true || "" }),
    })
      .unwrap()
      .then(() => {
        successCallback?.();
      })
      .catch((error) => {
        resendErrorMessage.current = serverErrorMessage(error);
        errorCallback?.();
      });
  };

  const resendSuccessCallback = (callback: any) => {
    toast.success(t("toast.code_sent"));
    callback?.();
  };

  const resendErrorCallback = (callback: any) => {
    toast.error(resendErrorMessage.current || t("toast.code_send_failed"));
    callback?.();
  };

  return (
    <LandingWrapper>
      <Formik
        initialValues={formInitialValues}
        validationSchema={YupValidationSchema}
        onSubmit={(values, { setSubmitting }) => {
          if (APP_CONFIG.TOASTS.INFO) toast.info(t("toast.verifying_code"));
          verifyEmailOtpApiTrigger({
            email: preLoginUserData?.email || "",
            email_otp: values.verification_code,
          })
            .unwrap()
            .then((data) => {
              if (
                !!(data as LegacyVerifyEmailOtpResponseSuccess)?.access_token
              ) {
                onUserTokenRetrievedSetupSessionNRedirect(
                  data as LegacyVerifyEmailOtpResponseSuccess,
                  !!msalAccounts && msalAccounts.length > 0
                    ? "azureAD"
                    : "pyDRF"
                );
                toast.success(t("toast.code_verified"));
              } else {
                toast.error(t("toast.code_verification_failed"), {
                  autoClose: 10000,
                });
              }
            })
            .catch((error) => {
              const errors = !!error?.data
                ? serializeErrorKeyValues(error?.data?.message)
                : [t("loginScreen.toast.login_failed")];
              const error_message = errors.join(" ");
              toast.error(
                error_message.toLowerCase().indexOf("invalid") > -1
                  ? t("toast.code_invalid")
                  : errors?.join(" "),
                { autoClose: 10000 }
              );
            })
            .finally(() => {
              setSubmitting(false);
            });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          dirty,
        }) => {
          const invalidFields = Object.keys(errors).filter(
            (key) => !!errors[key as TFormFieldNames]
          );
          const isFormValid = invalidFields.length === 0 && dirty;

          return (
            <form
              className="p-8 bg-white grid grid-cols-6 gap-6 rounded-3xl shadow-2xl"
              onSubmit={handleSubmit}
            >
              <h2 className="col-span-6 text-3xl font-bold text-heading-black">
                {t("heading")}
              </h2>

              <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
                {t("sub_heading_1")}
                {verifyMethod === "email" && (
                  <span className="font-semibold text-gray-600">
                    {" "}
                    {preLoginUserData?.email || ""}
                  </span>
                )}
                {verifyMethod === "email" && ". "} {t("sub_heading_2")}
              </p>

              <LandingFormFieldInput
                ref={fieldRef}
                label={t("verification_code")}
                type="text"
                id="VerificationCode"
                name="verification_code"
                placeholder={t("verification_code_placeholder")}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.verification_code}
                touched={touched.verification_code}
                error={errors.verification_code}
                customInputClass="tracking-widest placeholder-tracking-normal"
              />

              {isLoadingResendEmailOtp || isLoadingVerifyEmailOtp ? (
                <div className="col-span-6 flex justify-center">
                  <img
                    src={svgIcons.LoadingAnimation}
                    alt="Loading animation"
                    className="w-12 h-12"
                  />
                </div>
              ) : (
                <LandingFormFieldSubmit
                  label={t("submit_button")}
                  variant="primary"
                  disabled={!isFormValid || isSubmitting}
                />
              )}

              <ResendCode
                isLoading={isLoadingResendEmailOtp || isLoadingVerifyEmailOtp}
                onResendCode={handleResendCode}
                resendSuccessCallback={resendSuccessCallback}
                resendErrorCallback={resendErrorCallback}
              />
            </form>
          );
        }}
      </Formik>
    </LandingWrapper>
  );
};

export default ScreenVerify2FA;
